import { type ReactNode, useCallback, useRef } from "react";
import { isEqual } from "lodash";
import { type UseFormReturn } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import { useIsCustomThemesEnabled } from "@/react/components/SettingsApp/Site/Pages/hooks/useIsCustomThemesEnabled";
import { usePage } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import { useCustomThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/CustomThemeConfirmationModal";
import { usePageThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/PageThemeConfirmationModal";
import { useSiteThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/SiteThemeConfirmationModal";
import {
  type BaseThemeFormData,
  BaseThemeFormWrapper,
  useThemeFormHelpers,
} from "@circle-react-shared/BuilderUI/Theme/BaseThemeFormWrapper";
import type { CssVariables } from "../SiteThemes/constants";
import { FormWrapperFooter } from "./FormWrapperFooter";
import { CSS_VARIABLE_LOOKUP } from "./constants";
import { useCreateGoogleFontsFromStore } from "./hooks/useCreateGoogleFontsFromStore";

export interface FormData extends BaseThemeFormData {
  theme: CssVariables;
}

interface FormWrapperProps {
  children: ReactNode;
}

export const FormWrapper = ({ children }: FormWrapperProps) => {
  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const { page, isLoading: isPageLoading } = usePage(pageId);
  const {
    googleFonts,
    currentCanvasAppearance,
    currentCanvasTheme,
    updateCurrentCanvasThemeVariables,
    toggleCurrentCanvasTheme,
  } = useThemeFormHelpers();

  const isCustomThemesEnabled = useIsCustomThemesEnabled();
  const { show: showPageThemeConfirmationModal } =
    usePageThemeConfirmationModal();
  const { show: showCustomThemeConfirmationModal } =
    useCustomThemeConfirmationModal();
  const { show: showSiteThemeConfirmationModal } =
    useSiteThemeConfirmationModal();
  const {
    mutate: createGoogleFontsFromStore,
    isLoading: isCreatingGoogleFonts,
  } = useCreateGoogleFontsFromStore();

  const formMethodsRef = useRef<UseFormReturn<FormData>>();
  const pageThemeVariables = page?.theme.css_variables;
  const currentCanvasThemeVariables = currentCanvasTheme.cssVariables;

  const processVariableUpdate = useCallback(
    (fieldValue: string, variableName: string) => {
      const variableLookup = CSS_VARIABLE_LOOKUP[variableName];

      if (!variableLookup) return;

      const key = variableLookup.category;
      const cssVariable = variableLookup.variable;

      if (!key || !cssVariable) return;

      if (key === "appearance") {
        updateCurrentCanvasThemeVariables(
          currentCanvasAppearance,
          cssVariable,
          fieldValue,
        );
      } else {
        updateCurrentCanvasThemeVariables(key, cssVariable, fieldValue);
      }
    },
    [updateCurrentCanvasThemeVariables, currentCanvasAppearance],
  );

  const handleFormReady = useCallback((methods: UseFormReturn<FormData>) => {
    formMethodsRef.current = methods;
  }, []);

  const handleDiscard = useCallback(() => {
    if (isPageLoading || !page) return;

    const { id, theme_name, css_variables } = page.theme;

    toggleCurrentCanvasTheme({
      value: id,
      name: theme_name,
      cssVariables: css_variables,
    });

    formMethodsRef.current?.reset({
      theme: css_variables,
    });
  }, [isPageLoading, page, toggleCurrentCanvasTheme]);

  const handlePageThemUpdate = useCallback(() => {
    if (!formMethodsRef.current) return;
    const isDirty = formMethodsRef.current.formState.isDirty;

    if (isDirty && isCustomThemesEnabled) {
      void showCustomThemeConfirmationModal({
        appearance: currentCanvasAppearance,
        cssVariables: currentCanvasThemeVariables,
        reset: () => formMethodsRef.current?.reset(),
        scope: "page",
        toggleCurrentCanvasTheme,
        createGoogleFontsFromStore,
        googleFonts: Array.from(googleFonts.values()),
        isCreatingGoogleFonts,
        pageId,
      });
    } else {
      void showPageThemeConfirmationModal({
        currentCanvasTheme,
        appearance: currentCanvasAppearance,
        pageId,
      });
    }
  }, [
    isCustomThemesEnabled,
    currentCanvasTheme,
    currentCanvasAppearance,
    currentCanvasThemeVariables,
    toggleCurrentCanvasTheme,
    pageId,
    showCustomThemeConfirmationModal,
    showPageThemeConfirmationModal,
    googleFonts,
    createGoogleFontsFromStore,
    isCreatingGoogleFonts,
  ]);

  const handleSiteThemeUpdate = useCallback(() => {
    if (!formMethodsRef.current) return;
    const isDirty = formMethodsRef.current.formState.isDirty;

    if (isDirty && isCustomThemesEnabled) {
      void showCustomThemeConfirmationModal({
        appearance: currentCanvasAppearance,
        cssVariables: currentCanvasThemeVariables,
        reset: () => formMethodsRef.current?.reset(),
        scope: "site",
        toggleCurrentCanvasTheme,
        createGoogleFontsFromStore,
        googleFonts: Array.from(googleFonts.values()),
        isCreatingGoogleFonts,
        pageId,
      });
    } else {
      void showSiteThemeConfirmationModal({
        currentCanvasTheme,
        appearance: currentCanvasAppearance,
        cssVariables: currentCanvasThemeVariables,
      });
    }
  }, [
    pageId,
    googleFonts,
    createGoogleFontsFromStore,
    isCreatingGoogleFonts,
    isCustomThemesEnabled,
    currentCanvasTheme,
    currentCanvasAppearance,
    currentCanvasThemeVariables,
    toggleCurrentCanvasTheme,
    showCustomThemeConfirmationModal,
    showSiteThemeConfirmationModal,
  ]);

  const handleValueChange = useCallback(
    (methods: UseFormReturn<FormData>) => {
      if (!isEqual(pageThemeVariables, currentCanvasThemeVariables)) {
        methods.setValue("theme", currentCanvasThemeVariables, {
          shouldDirty: true,
        });
      }
    },
    [pageThemeVariables, currentCanvasThemeVariables],
  );

  const renderFooter = useCallback(
    () => (
      <FormWrapperFooter
        isPageLoading={isPageLoading}
        onDiscard={handleDiscard}
        onUpdatePageTheme={handlePageThemUpdate}
        onUpdateSiteTheme={handleSiteThemeUpdate}
      />
    ),
    [isPageLoading, handleDiscard, handlePageThemUpdate, handleSiteThemeUpdate],
  );

  return (
    <BaseThemeFormWrapper<FormData>
      defaultValues={{
        theme: pageThemeVariables || {},
      }}
      processVariableUpdate={processVariableUpdate}
      onFormReady={handleFormReady}
      onValueChange={handleValueChange}
      footerContent={renderFooter}
    >
      {children}
    </BaseThemeFormWrapper>
  );
};
